"use client";

import React, { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useFormContext, Controller } from 'react-hook-form';
import GeneratorPage from '../GeneratorPage';
import { useLocaleNavigation } from '@/lib/navigation';
import { CATEGORIES } from '@/constants/categories';
import { generateFunnyNames } from '@/lib/funnyNameGenerator';
import { Input, Select, Button } from '@/components/design-system';
import { Copy, Check } from 'lucide-react';

export default function FunnyNamesGenerator() {
    const t = useTranslations('funny-names');
    const locale = useLocale();
    const { getNavigationPath } = useLocaleNavigation();
    const [generatedNames, setGeneratedNames] = useState<string[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const category = CATEGORIES.entertainment;
    const generator = category.generators.find(g => g.id === 'funny-names');

    const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'tr', 'ar', 'hi', 'ja', 'ko', 'id', 'vi', 'ru', 'it', 'pl', 'nl'];
    const defaultLanguage = supportedLocales.includes(locale) ? locale : 'en';

    // Default values for the form
    const defaultValues = {
        style: 'any',
        count: 10,
        language: defaultLanguage
    };

    if (!generator) return null;

    const handleGenerate = async (e: React.FormEvent, data: any) => {
        // Prevent default submission if event is present
        if (e && e.preventDefault) e.preventDefault();

        setIsGenerating(true);

        // Use data from React Hook Form or fallback
        const formData = data || defaultValues;

        // Simulate network delay for better UX
        await new Promise(resolve => setTimeout(resolve, 600));

        const names = generateFunnyNames({
            count: Number(formData.count),
            style: formData.style,
            locale: formData.language
        });

        setGeneratedNames(names);
        setIsGenerating(false);
    };

    const handleCopyAll = async () => {
        await navigator.clipboard.writeText(generatedNames.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const faqs = t.raw('faqs') as { question: string; answer: string }[];
    const similarGenerators = category.generators
        .filter(g => g.id !== 'funny-names')
        .slice(0, 3)
        .map(g => ({
            id: g.id,
            name: t(`similarHelpers.${g.id}.name` as any) || g.name['en'],
            description: t(`similarHelpers.${g.id}.description` as any) || g.description['en'],
            icon: g.icon,
            href: getNavigationPath(g.id),
            category: category.name['en']
        }));

    // Form Content Component to access context provided by GeneratorPage
    const FormContent = () => {
        const { control } = useFormContext();
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                    <label className="block mb-2 font-medium">{t('form.style')}</label>
                    <Controller
                        name="style"
                        control={control}
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                <option value="any">{t('form.styleOptions.any')}</option>
                                <option value="absurd">{t('form.styleOptions.absurd')}</option>
                                <option value="silly">{t('form.styleOptions.silly')}</option>
                            </Select>
                        )}
                    />
                </div>
                <div>
                    <label className="block mb-2 font-medium">{t('form.language')}</label>
                    <Controller
                        name="language"
                        control={control}
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                <option value="en">{t('form.languageOptions.en')}</option>
                                <option value="es">{t('form.languageOptions.es')}</option>
                                <option value="fr">{t('form.languageOptions.fr')}</option>
                                <option value="de">{t('form.languageOptions.de')}</option>
                                <option value="pt">{t('form.languageOptions.pt')}</option>
                                <option value="tr">{t('form.languageOptions.tr')}</option>
                                <option value="ar">{t('form.languageOptions.ar')}</option>
                                <option value="hi">{t('form.languageOptions.hi')}</option>
                                <option value="ja">{t('form.languageOptions.ja')}</option>
                                <option value="ko">{t('form.languageOptions.ko')}</option>
                                <option value="id">{t('form.languageOptions.id')}</option>
                                <option value="vi">{t('form.languageOptions.vi')}</option>
                                <option value="ru">{t('form.languageOptions.ru')}</option>
                                <option value="it">{t('form.languageOptions.it')}</option>
                                <option value="pl">{t('form.languageOptions.pl')}</option>
                                <option value="nl">{t('form.languageOptions.nl')}</option>
                            </Select>
                        )}
                    />
                </div>
                <div>
                    <label className="block mb-2 font-medium">{t('form.count')}</label>
                    <Controller
                        name="count"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="number"
                                min={1}
                                max={50}
                                value={field.value}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                placeholder={t('form.countPlaceholder')}
                            />
                        )}
                    />
                </div>
            </div>
        );
    };

    const customResultsRenderer = () => {
        if (!generatedNames || generatedNames.length === 0) {
            return (
                <div className="text-center text-gray-500 py-8">
                    {t("results.noResults")}
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                    <Button type="button" size="sm" variant="outline" onClick={handleCopyAll}>
                        {copied ? (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                {t("results.copyAllSuccess")}
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" />
                                {t("results.copyAll")}
                            </>
                        )}
                    </Button>
                </div>
                <div className="space-y-2">
                    {generatedNames.map((name, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-500 transition-colors group">
                            <span className="font-medium text-gray-900">{name}</span>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(name);
                                }}
                                className="p-2 text-gray-400 hover:text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={t("form.copyButton")}
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <GeneratorPage
            title={t('title')}
            description={t('description')}
            icon={generator.icon}
            category={category}
            generatedData={generatedNames.join('\n')}
            isGenerating={isGenerating}
            onGenerate={handleGenerate}
            generateButtonText={t('form.generateButton')}
            faqs={faqs}
            similarGenerators={similarGenerators}
            resultsRenderer={customResultsRenderer()}
            formConfig={{
                values: defaultValues,
                fields: [
                    {
                        key: 'style',
                        label: t('form.style'),
                        type: 'select',
                        options: [
                            { label: t('form.styleOptions.any'), value: 'any' },
                            { label: t('form.styleOptions.absurd'), value: 'absurd' },
                            { label: t('form.styleOptions.silly'), value: 'silly' }
                        ]
                    },
                    {
                        key: 'language',
                        label: t('form.language'),
                        type: 'select',
                        options: [
                            { label: t('form.languageOptions.en'), value: 'en' },
                            { label: t('form.languageOptions.es'), value: 'es' },
                            { label: t('form.languageOptions.fr'), value: 'fr' },
                            { label: t('form.languageOptions.de'), value: 'de' },
                            { label: t('form.languageOptions.pt'), value: 'pt' },
                            { label: t('form.languageOptions.tr'), value: 'tr' },
                            { label: t('form.languageOptions.ar'), value: 'ar' },
                            { label: t('form.languageOptions.hi'), value: 'hi' },
                            { label: t('form.languageOptions.ja'), value: 'ja' },
                            { label: t('form.languageOptions.ko'), value: 'ko' },
                            { label: t('form.languageOptions.id'), value: 'id' },
                            { label: t('form.languageOptions.vi'), value: 'vi' },
                            { label: t('form.languageOptions.ru'), value: 'ru' },
                            { label: t('form.languageOptions.it'), value: 'it' },
                            { label: t('form.languageOptions.pl'), value: 'pl' },
                            { label: t('form.languageOptions.nl'), value: 'nl' }
                        ],
                        defaultValue: defaultValues.language
                    },
                    {
                        key: 'count',
                        label: t('form.count'),
                        type: 'number',
                        validation: {
                            required: true,
                            min: 1,
                            max: 50
                        }
                    }
                ],
                onChange: () => { }
            }}
            metadata={{
                keywords: ['funny', 'absurd', 'silly', 'names'],
                examples: ['Potato Face', 'Sir Wiggles', 'Boaty McBoatface'],
                useCases: ['Comedy', 'Pranks', 'Usernames']
            }}
        >
            <FormContent />
        </GeneratorPage>
    );
}
